/**
 * AuthContext
 *
 * Provides authentication state and actions to the entire component tree.
 * Wraps Amplify Auth v6 functions for sign-in, sign-up, sign-out,
 * password reset, and session management.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 7.2, 7.3, 7.5
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode as amplifyResendSignUpCode,
  confirmSignIn as amplifyConfirmSignIn,
  signOut as amplifySignOut,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  signInWithRedirect,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import {
  type AccessHat,
  type Capability,
  type LifecycleFacet,
  deriveRoleSet,
  deriveRoles,
  capabilitiesFromRoles,
  highestStandingRole,
  parseLifecycleFacets,
} from '@/lib/accessCapabilities';
// Per-tenant cache that must not survive a tenant switch — see the call site in
// `handleSwitchTenant`. Imported from the LEAF cache module rather than from
// `useMyEntitlements`, which would introduce an
// AuthContext -> hook -> useAuth -> AuthContext import cycle.
import { invalidateMyEntitlementsCache } from '@/lib/entitlementCache';

export type { AccessHat, Capability, LifecycleFacet } from '@/lib/accessCapabilities';

// ── Role Types (web-local adapter) ────────────────────────────────────────────
//
// The canonical role model in @iap/shared was decontaminated (see the
// mobile-identity-bifurcation spec) to an additive four-role SET —
// `admin | poweruser | director | judge` — that intentionally drops
// `contestant`/`applicant` as roles (they are lifecycle stages, not role hats).
//
// The web portal, however, still keys routing, sidebars, ProtectedRoute, and
// settings tabs on a SINGLE effective role that treats `contestant` as the
// default non-elevated role. To keep the web portal on its established
// single-role contract without reintroducing `contestant` into the shared
// canonical vocabulary, the web-facing `RoleType` / `GROUP_TO_ROLE` /
// `ROLE_PRIORITY` / `deriveRole` are defined LOCALLY here. The group-based
// `isAdmin` / `isElevated` predicates are unchanged by the decontamination and
// continue to be re-exported from @iap/shared.
//
// A future, deliberate web decontamination (mirroring mobile-identity-
// bifurcation task 1.3) can migrate these consumers to the additive Role set.

export { isAdmin, isElevated } from '../lib/accessCapabilities';

/** Web portal effective-role vocabulary. `contestant` is the default non-elevated role. */
export type RoleType = 'admin' | 'poweruser' | 'judge' | 'contestant';

/** Maps Cognito group names to the web `RoleType`. `Applicants` → `contestant`. */
export const GROUP_TO_ROLE: Readonly<Record<string, RoleType>> = {
  Admins: 'admin',
  PowerUsers: 'poweruser',
  Judges: 'judge',
  Applicants: 'contestant',
};

/** Role priority order — highest priority first. */
export const ROLE_PRIORITY: RoleType[] = ['admin', 'poweruser', 'judge', 'contestant'];

/**
 * Derive the single effective web role from a list of Cognito groups.
 *
 * - Unknown group names are ignored; duplicates and order are irrelevant.
 * - The highest-priority recognized group wins (Admins > PowerUsers > Judges >
 *   Applicants).
 * - Defaults to `contestant` when no recognized group is present.
 */
export function deriveRole(groups: readonly string[]): RoleType {
  const roles = groups
    .map((g) => GROUP_TO_ROLE[g])
    .filter((r): r is RoleType => r !== undefined);
  for (const priority of ROLE_PRIORITY) {
    if (roles.includes(priority)) return priority;
  }
  return 'contestant';
}

// ── Interfaces ──────────────────────────────────────────────────────────────

export interface AuthUser {
  email: string;
  firstName?: string;
  lastName?: string;
  fullName: string;
  groups: string[];
  cognitoSub: string;
  /**
   * The lifecycle facets parsed from the server-verified `lifecycle` ID-token
   * claim (durable-portal-access). Sources `canSeePortal` off the retiring
   * `Applicants` group and onto the claim. Unrecognized/absent claim values
   * parse to `[]` (no portal).
   */
  lifecycleFacets: LifecycleFacet[];
  /**
   * The active tenant from the `custom:activeTenantId` ID-token claim
   * (tenant-foundation Task 5). Set server-side on login/switch after a
   * Membership is validated — never client-asserted. Absent until the Idaho
   * backfill (Task 8) stamps it; DARK until enforcement flips (Task 10).
   */
  activeTenantId?: string;
}

/**
 * Outcome of a `switchTenant` call (tenant-foundation Task 11). Fail-closed:
 * `success: false` (with an explanatory `message`) leaves the session's active
 * tenant unchanged — the caller holds no active Membership in the target Tenant,
 * or the switch/token-refresh failed.
 */
export interface SwitchTenantResult {
  success: boolean;
  activeTenantId?: string | null;
  membershipStatus?: string | null;
  message: string;
}

export interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  role: RoleType;
  isAdmin: boolean;
  isPowerUser: boolean;
  isJudge: boolean;
  isApplicant: boolean;
  isDirector: boolean;
  /**
   * True when the user is in the `PlatformAdmins` Cognito group — the
   * cross-tenant platform-operator tier (tenant-foundation Task 7), distinct
   * from a tenant's own `Admins`. Orthogonal to the tenant-scoped Role
   * vocabulary + capabilities; gates the platform-admin surface only.
   */
  isPlatformAdmin: boolean;
  /**
   * The multi-hat Role_Set derived from the user's Cognito groups
   * (`Admins → admin`, `PowerUsers → poweruser`, `Judges → judge`). Empty when
   * the user holds no recognized access group. Derived solely from
   * `cognito:groups`, never from `User.role`.
   */
  roleSet: ReadonlySet<AccessHat>;
  /** True when the user holds Portal_Access (Applicants group membership). */
  portalAccess: boolean;
  /** The set of named capabilities the user holds, computed from the Role_Set + Portal_Access. */
  capabilities: ReadonlySet<Capability>;
  /** Returns true if the user holds the given capability. */
  can: (capability: Capability) => boolean;
  /** True if the current session was established via a social/federated provider. */
  isFederatedSession: boolean;
  /** OAuth error message from redirect callback or Hub event, for inline display. */
  oauthError: string | null;
  /** Clear the current OAuth error (e.g., after the user dismisses it). */
  clearOAuthError: () => void;
  /** Returns true if sign-in requires a new password (admin-created user with temp password). */
  signIn: (email: string, password: string) => Promise<{ requiresNewPassword: boolean }>;
  /** Complete the NEW_PASSWORD_REQUIRED challenge after signIn returns requiresNewPassword: true. */
  completeNewPassword: (newPassword: string) => Promise<void>;
  /** Initiate OAuth sign-in with a social provider via Cognito Hosted UI redirect. */
  signInWithSocial: (provider: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => Promise<{ isSignUpComplete: boolean; nextStep: any }>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  /** Resend the sign-up verification code to the given email (Cognito ResendConfirmationCode). */
  resendSignUpCode: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  /**
   * Switch the caller's active Tenant (tenant-foundation Task 11, Req 19.3).
   * Calls `switchActiveTenant` — the server validates the caller holds an active
   * Membership in `tenantId` and re-mints the `custom:activeTenantId` claim —
   * then forces an Amplify token refresh so the fresh claim is parsed back onto
   * `user.activeTenantId` (every claim consumer, including the enforcement gate
   * and the switcher, then sees the new active tenant without a reload).
   * Fail-closed: an invalid/denied switch returns `success: false` and leaves
   * the session unchanged.
   */
  switchTenant: (tenantId: string) => Promise<SwitchTenantResult>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * `switchActiveTenant` mutation (tenant-foundation Task 5/11). Defined locally —
 * the web codegen client does not include it (the schema is hand-mirrored,
 * matching the season-ssot hook convention). Gated `@aws_cognito_user_pools`.
 */
const SWITCH_ACTIVE_TENANT_MUTATION = /* GraphQL */ `
  mutation SwitchActiveTenant($tenantId: ID!) {
    switchActiveTenant(tenantId: $tenantId) {
      success
      activeTenantId
      membershipStatus
      message
    }
  }
`;

interface SwitchActiveTenantData {
  switchActiveTenant: {
    success: boolean;
    activeTenantId: string | null;
    membershipStatus: string | null;
    message: string;
  } | null;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Parse an ID-token-like payload into an AuthUser.
 * Exported for testability (Property 3).
 */
export function parseAuthUser(payload: Record<string, unknown>): AuthUser {
  const email = ((payload['email'] as string) ?? '').trim().toLowerCase();
  const given = (payload['given_name'] as string) ?? '';
  const family = (payload['family_name'] as string) ?? '';
  // Prefer the claims-side name when present. If both given_name and
  // family_name are absent (legacy sessions predating Phase B), fall back to
  // the `name` claim so `fullName` stays populated for downstream consumers.
  const fullNameFromClaim = (payload['name'] as string) ?? '';
  const fullName =
    given && family ? `${given} ${family}` : fullNameFromClaim;
  const cognitoSub = (payload['sub'] as string) ?? '';
  const groups = (Array.isArray(payload['cognito:groups'])
    ? payload['cognito:groups']
    : []) as string[];
  // Server-verified lifecycle facet claim. Strict/typed parse — unknown or
  // absent values yield [] (no portal). Never throws.
  const lifecycleFacets = parseLifecycleFacets(payload['lifecycle']);

  const user: AuthUser = { email, fullName, cognitoSub, groups, lifecycleFacets };
  if (given) user.firstName = given;
  if (family) user.lastName = family;
  // Active tenant claim (tenant-foundation Task 5) — server-stamped, read-only
  // here. Trimmed; omitted when absent (no "default tenant").
  const activeTenantId = ((payload['custom:activeTenantId'] as string) ?? '').trim();
  if (activeTenantId) user.activeTenantId = activeTenantId;
  return user;
}

/**
 * Detect whether a JWT payload represents a federated (social) session.
 * Cognito adds an `identities` claim to the ID token for federated users.
 * Exported for testability.
 */
export function isFederatedPayload(payload: Record<string, unknown>): boolean {
  const identities = payload['identities'];
  return Array.isArray(identities) && identities.length > 0;
}

/**
 * Parse OAuth error parameters from the current URL search params.
 * Cognito appends `error` and `error_description` on OAuth failures.
 * Returns null if no error is present.
 */
export function parseOAuthErrorFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  const errorDescription = params.get('error_description');
  if (!error) return null;
  // Clean the URL by removing error params without triggering a reload
  const url = new URL(window.location.href);
  url.searchParams.delete('error');
  url.searchParams.delete('error_description');
  window.history.replaceState({}, '', url.toString());
  return errorDescription || error;
}

// ── Provider ────────────────────────────────────────────────────────────────

export interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [federated, setFederated] = useState(false);

  // Restore session on mount
  const restoreSession = useCallback(async () => {
    try {
      await getCurrentUser(); // throws if not signed in
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload;
      if (payload) {
        setUser(parseAuthUser(payload as Record<string, unknown>));
        setFederated(isFederatedPayload(payload as Record<string, unknown>));
      } else {
        setUser(null);
        setFederated(false);
      }
    } catch {
      // Not signed in or token refresh failed — clear state
      setUser(null);
      setFederated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Parse OAuth error from URL on mount (Cognito appends error params on failure)
  useEffect(() => {
    const urlError = parseOAuthErrorFromUrl();
    if (urlError) {
      setOauthError(urlError);
    }
  }, []);

  // Listen for Amplify Hub auth events for OAuth redirect completion/failure
  useEffect(() => {
    const cleanup = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          // OAuth redirect completed — refresh session
          try {
            const session = await fetchAuthSession();
            const tokenPayload = session.tokens?.idToken?.payload;
            if (tokenPayload) {
              setUser(parseAuthUser(tokenPayload as Record<string, unknown>));
              setFederated(isFederatedPayload(tokenPayload as Record<string, unknown>));
            }
          } catch {
            // Session fetch failed after redirect — leave user as null
          }
          break;
        case 'signInWithRedirect_failure':
          // OAuth redirect failed — capture error for inline display
          setOauthError('Social sign-in failed. Please try again.');
          break;
      }
    });
    return cleanup;
  }, []);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  // ── Auth actions ────────────────────────────────────────────────────────

  /**
   * Normalize an email at the AuthContext boundary so every Amplify Auth
   * call (signUp, signIn, confirmSignUp, resetPassword,
   * confirmResetPassword) sees a canonical lowercase value. Cognito's SRP
   * password verifier binds to the exact Username chosen at sign-up, so a
   * mixed-case sign-up creates an account that fails to log in via SRP on
   * any later case drift (the documented "Betsy + Timi SRP lockout" from
   * `infrastructure/lambda/admin-user-management/index.ts`, and the
   * Angie Palmer bug that triggered this fix). The mobile app and the
   * admin-user-management Lambda already normalize at their boundaries —
   * this is the matching shim for the public web flows. See
   * `qa-backlog/email-case-normalization.md` for the full context.
   */
  function normalizeEmail(email: string): string {
    return email.trim().toLowerCase();
  }

  const handleSignIn = useCallback(async (email: string, password: string) => {
    const username = normalizeEmail(email);
    // Clear any stale session before attempting sign-in
    try {
      await amplifySignOut();
    } catch {
      // Ignore — no session to clear
    }
    const result = await amplifySignIn({ username, password });

    // Admin-created users with temporary passwords trigger this challenge
    if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { requiresNewPassword: true };
    }

    // After sign-in, fetch session to populate user
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload;
    if (payload) {
      setUser(parseAuthUser(payload as Record<string, unknown>));
    }
    return { requiresNewPassword: false };
  }, []);

  const handleCompleteNewPassword = useCallback(async (newPassword: string) => {
    await amplifyConfirmSignIn({ challengeResponse: newPassword });
    // After completing the challenge, fetch session to populate user
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload;
    if (payload) {
      setUser(parseAuthUser(payload as Record<string, unknown>));
    }
  }, []);

  const handleSignUp = useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string
    ) => {
      const username = normalizeEmail(email);
      const derivedFullName = `${firstName} ${lastName}`;
      const result = await amplifySignUp({
        username,
        password,
        options: {
          userAttributes: {
            email: username,
            given_name: firstName,
            family_name: lastName,
            name: derivedFullName,
          },
        },
      });
      return {
        isSignUpComplete: result.isSignUpComplete,
        nextStep: result.nextStep,
      };
    },
    []
  );

  const handleConfirmSignUp = useCallback(async (email: string, code: string) => {
    await amplifyConfirmSignUp({ username: normalizeEmail(email), confirmationCode: code });
  }, []);

  const handleResendSignUpCode = useCallback(async (email: string) => {
    await amplifyResendSignUpCode({ username: normalizeEmail(email) });
  }, []);

  const handleSignOut = useCallback(async () => {
    await amplifySignOut();
    setUser(null);
    setFederated(false);
    setOauthError(null);
  }, []);

  const handleResetPassword = useCallback(async (email: string) => {
    await amplifyResetPassword({ username: normalizeEmail(email) });
  }, []);

  const handleConfirmResetPassword = useCallback(
    async (email: string, code: string, newPassword: string) => {
      await amplifyConfirmResetPassword({
        username: normalizeEmail(email),
        confirmationCode: code,
        newPassword,
      });
    },
    [],
  );

  /**
   * Switch the caller's active Tenant (tenant-foundation Task 11, Req 19.3).
   *
   * Two-step: (1) call `switchActiveTenant`, which re-validates the caller's
   * active Membership server-side and re-mints the `custom:activeTenantId`
   * user attribute; (2) force an Amplify token refresh so the pre-token
   * trigger stamps the fresh claim into a NEW ID token, then re-parse that
   * token onto the local `AuthUser`. Fail-closed — a denied switch returns
   * `success: false` and leaves the session's active tenant untouched.
   */
  const handleSwitchTenant = useCallback(
    async (tenantId: string): Promise<SwitchTenantResult> => {
      const client = generateClient();
      let result: SwitchActiveTenantData['switchActiveTenant'];
      try {
        const res = (await client.graphql({
          query: SWITCH_ACTIVE_TENANT_MUTATION,
          variables: { tenantId },
          authMode: 'userPool',
        })) as GraphQLResult<SwitchActiveTenantData>;
        result = res.data?.switchActiveTenant ?? null;
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Unable to switch tenant.';
        return { success: false, message };
      }

      if (!result?.success) {
        return {
          success: false,
          activeTenantId: result?.activeTenantId ?? null,
          membershipStatus: result?.membershipStatus ?? null,
          message: result?.message ?? 'Unable to switch tenant.',
        };
      }

      // Drop the cached entitlements BEFORE the new claim lands
      // (product-entitlement-enforcement Req 3.3). Entitlements are PER-TENANT,
      // so a cache surviving a switch would let the previous organization's
      // subscriptions gate this one's surfaces — the one cache whose lifetime
      // must be tied to the active tenant rather than to the session. The
      // membership cache deliberately survives: that list is the caller's own
      // memberships across every tenant, identical whichever one is active.
      invalidateMyEntitlementsCache();

      // Force a token refresh so the pre-token-generation trigger re-mints the
      // custom:activeTenantId claim into a fresh ID token; then re-parse it onto
      // the AuthUser so every claim consumer sees the new active tenant without
      // a full reload (Req 19.3).
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const payload = session.tokens?.idToken?.payload;
        if (payload) {
          setUser(parseAuthUser(payload as Record<string, unknown>));
        }
      } catch {
        // The server-side switch succeeded but the local refresh failed; the
        // next natural token refresh will pick up the claim. Report success so
        // the caller does not retry the mutation.
      }

      return {
        success: true,
        activeTenantId: result.activeTenantId ?? tenantId,
        membershipStatus: result.membershipStatus ?? null,
        message: result.message,
      };
    },
    [],
  );

  /** Initiate OAuth sign-in with a social provider via Cognito Hosted UI redirect. */
  const handleSignInWithSocial = useCallback(async (provider: string) => {
    setOauthError(null);
    try {
      await signInWithRedirect({ provider: { custom: provider } });
    } catch (err: any) {
      setOauthError(err?.message || 'Failed to initiate social sign-in.');
    }
  }, []);

  const clearOAuthError = useCallback(() => {
    setOauthError(null);
  }, []);

  // ── Derived state ───────────────────────────────────────────────────────

  const contextValue = useMemo<AuthContextValue>(() => {
    const groups = user?.groups ?? [];
    const lifecycleFacets = user?.lifecycleFacets ?? [];
    // Legacy 4-hat derivation retained ONLY to keep `roleSet`/`portalAccess`
    // available for existing consumers during the transition. It is NO LONGER
    // the source of `canSeePortal`.
    const { roleSet, portalAccess } = deriveRoleSet(groups);
    // Facet-sourced capabilities (durable-portal-access, Req 1.1/1.2/6.1):
    // `canSeePortal` derives from the server-verified lifecycle claim facets,
    // not from `Applicants` group membership. Roles come from the canonical
    // two-value `deriveRoles`.
    const capabilities = capabilitiesFromRoles(deriveRoles(groups), lifecycleFacets);
    // Backward-compatible single role: the highest-standing member of the
    // Role_Set, falling back to `contestant`. Equals the legacy `deriveRole`
    // output so existing single-role consumers keep working (Req 13).
    const role = highestStandingRole(roleSet) as RoleType;
    return {
      user,
      isAuthenticated: user !== null,
      isLoading,
      role,
      isAdmin: role === 'admin',
      isPowerUser: role === 'poweruser',
      isJudge: role === 'judge',
      isApplicant: groups.includes('Applicants'),
      isDirector: groups.includes('Admins'),
      isPlatformAdmin: groups.includes('PlatformAdmins'),
      roleSet,
      portalAccess,
      capabilities,
      can: (capability: Capability) => capabilities.has(capability),
      isFederatedSession: federated,
      oauthError,
      clearOAuthError,
      signIn: handleSignIn,
      completeNewPassword: handleCompleteNewPassword,
      signInWithSocial: handleSignInWithSocial,
      signUp: handleSignUp,
      confirmSignUp: handleConfirmSignUp,
      resendSignUpCode: handleResendSignUpCode,
      signOut: handleSignOut,
      resetPassword: handleResetPassword,
      confirmResetPassword: handleConfirmResetPassword,
      switchTenant: handleSwitchTenant,
    };
  }, [
    user,
    isLoading,
    federated,
    oauthError,
    clearOAuthError,
    handleSignIn,
    handleCompleteNewPassword,
    handleSignInWithSocial,
    handleSignUp,
    handleConfirmSignUp,
    handleResendSignUpCode,
    handleSignOut,
    handleResetPassword,
    handleConfirmResetPassword,
    handleSwitchTenant,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuthContext — low-level context consumer.
 * Prefer the `useAuth` hook from `@/hooks/useAuth`.
 */
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

/**
 * useActiveTenantId — the viewer's `custom:activeTenantId` claim, read WITHOUT
 * requiring an AuthProvider (tenant-foundation Task 7).
 *
 * The tenant-scoped `resolveActiveCompetitionId(year, activeTenantId)` callers
 * need ONLY this one claim. Reading it through `useAuth()` would make every such
 * hook/surface THROW when rendered outside an AuthProvider — a hard new coupling
 * for a purely additive, defaulting concern. This reader returns `undefined`
 * instead, which `competitionSlugForTenant` maps to the founding Idaho binding —
 * exactly the sanctioned back-compat default for an unresolved viewer.
 *
 * Use this (not `useAuth`) when all you need is the active tenant.
 */
export function useActiveTenantId(): string | undefined {
  return useContext(AuthContext)?.user?.activeTenantId;
}

export default AuthContext;
